const visFiltersYieldToMain = () => {
  const yieldFn = globalThis.scheduler?.yield
  if (typeof yieldFn === 'function') return yieldFn.call(globalThis.scheduler)
  return new Promise(resolve => {
    setTimeout(resolve, 0)
  })
}

class VisActiveFiltersSearch extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return

    this.tabs = [...this.querySelectorAll('.page-filters-search-tabs__item[data-tab-id]')]
    this.panels = [...this.querySelectorAll('.wd-vis-filter-search-list__panel')]
    this.pageTabs = this.querySelector('scroll-tabs.page-filters-search-tabs')
    this.search = this.querySelector('filters-model-search')
    this.contentHost = this.querySelector('[data-item-content-host]')
    this.contentCache = new Map()
    const initialContent = this.contentHost?.querySelector('.wd-vis-filter-search-list__item-content')
    if (initialContent?.id) this.contentCache.set(initialContent.id, initialContent)
    this.activeContentId = initialContent?.id || ''
    this.pendingContentId = this.activeContentId
    this.mountQueued = false

    this.initialized = true
    this.listenerAbort = new AbortController()
    const { signal } = this.listenerAbort

    this.pageTabs?.addEventListener('scroll-tabs:select', this.onPageTabsSelect, { signal })
    this.search?.addEventListener('filters-model-search:select', this.onSearchSelect, { signal })
    this.addEventListener('click', this.onClick, { signal })
    this.addEventListener('keydown', this.onKeydown, { signal })

    if (!this.tabs.length || !this.panels.length) {
      const initialItem = this.querySelector('.wd-vis-filter-search-list__item[data-controller-id].selected')
      if (initialItem) this.showItemContent(initialItem)
      return
    }

    const initialTab = this.tabs.find(tab => tab.classList.contains('selected')) || this.tabs[0]
    if (initialTab) this.activateTabPanel(initialTab)
  }

  disconnectedCallback() {
    this.listenerAbort?.abort()
    this.contentCache?.clear()
    this.pendingContentId = ''
    this.mountQueued = false
  }

  onPageTabsSelect = event => {
    this.activateTabPanel(event.detail.tab)
  }

  onSearchSelect = event => {
    if (!this.selectModel(event.detail.groupName, event.detail.shortName)) return
    window.requestAnimationFrame(() => this.scrollToSelectedPosition())
  }

  scrollToSelectedPosition() {
    const target = this.tabs.length
      ? this.pageTabs || this.querySelector('scroll-tabs.page-filters-search-tabs')
      : this.querySelector('.wd-vis-filter-search-list__splide')
    if (!target) return

    const nav = document.querySelector('.wd-vis-navigation-bar-container')
    const offset = nav instanceof HTMLElement ? nav.getBoundingClientRect().height : 0
    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset - 20)
    window.scrollTo({ top, behavior: 'smooth' })
  }

  onClick = event => {
    const item = event.target.closest('.wd-vis-filter-search-list__item[data-controller-id]')
    if (!item || !this.contains(item)) return
    this.selectItem(item)
  }

  onKeydown = event => {
    const item = event.target.closest('.wd-vis-filter-search-list__item[data-controller-id]')
    if (!item || !this.contains(item) || !['Enter', ' '].includes(event.key)) return
    event.preventDefault()
    this.selectItem(item)
  }

  selectModel(groupName, shortName, sourceItem) {
    const tab = this.tabs.find(item => item.dataset.searchGroup === groupName)
    if (tab) this.selectTab(tab)

    const scope = tab ? this.panels.find(panel => panel.id === tab.dataset.tabId) : this
    if (!scope) return false

    const item = this.ensureSlotModelItem(scope, groupName, shortName, sourceItem)
    if (!item) return false

    this.selectItem(item)
    this.moveSliderToItem(item)
    return true
  }

  findSliderItem(scope, shortName) {
    return [...scope.querySelectorAll('.wd-vis-filter-search-list__item[data-controller-id]')].find(
      card => card.dataset.searchShortName === shortName
    )
  }

  getSlotModelItem(scope) {
    return (
      scope.querySelector('.wd-vis-filter-search-list__item--slot') ||
      [...scope.querySelectorAll('.wd-vis-filter-search-list__item[data-controller-id]')].at(13) ||
      null
    )
  }

  findModalSourceItem(groupName, shortName) {
    const modal = document.querySelector(
      `vis-active-filters-search-all-modal[data-controller-id="${CSS.escape(this.id)}"]`
    )
    return [...(modal?.querySelectorAll('[data-all-systems-item]') || [])].find(
      item => item.dataset.searchGroup === groupName && item.dataset.searchShortName === shortName
    )
  }

  matchesItemContent(item, groupName, shortName) {
    return (
      item.dataset.searchShortName === shortName &&
      (!groupName || !item.dataset.searchGroup || item.dataset.searchGroup === groupName)
    )
  }

  findItemContentId(groupName, shortName) {
    const live = [...(this.contentHost?.querySelectorAll('[data-item-content-id]') || [])].find(item =>
      this.matchesItemContent(item, groupName, shortName)
    )
    if (live) return live.dataset.itemContentId || live.id || ''

    const template = [...this.querySelectorAll('template[data-item-content-id]')].find(item =>
      this.matchesItemContent(item, groupName, shortName)
    )
    return template?.dataset.itemContentId || ''
  }

  getItemContentTemplate(targetId) {
    return this.querySelector(`template[data-item-content-id="${CSS.escape(targetId)}"]`)
  }

  fillSlotModelItem(item, source, contentId) {
    item.dataset.controllerId = contentId
    item.dataset.searchGroup = source.dataset.searchGroup
    item.dataset.searchShortName = source.dataset.searchShortName

    const title = item.querySelector('.wd-vis-filter-search-list__item-title')
    const imageWrap = item.querySelector('.wd-vis-filter-search-list__item-image')
    const sourceTitle = source.querySelector('.wd-vis-filter-search-all-modal__item-title')
    const sourceImage = source.querySelector('.wd-vis-filter-search-all-modal__item-image')

    if (title) title.textContent = sourceTitle?.textContent?.trim() || source.dataset.searchShortName
    if (imageWrap && sourceImage) imageWrap.replaceChildren(...[...sourceImage.cloneNode(true).children])
  }

  ensureSlotModelItem(scope, groupName, shortName, sourceItem) {
    const existing = this.findSliderItem(scope, shortName)
    if (existing) return existing

    const slotItem = this.getSlotModelItem(scope)
    const source = sourceItem || this.findModalSourceItem(groupName, shortName)
    const contentId = this.findItemContentId(groupName, shortName)
    if (!slotItem || !source || !contentId) return null

    this.fillSlotModelItem(slotItem, source, contentId)
    return slotItem
  }

  selectTab(tab) {
    this.pageTabs?.selectTab?.(tab, { silent: true })
    this.activateTabPanel(tab)
  }

  activateTabPanel(tab) {
    if (!tab) return

    const targetId = tab.dataset.tabId
    const targetPanel = this.panels.find(panel => panel.id === targetId)
    if (!targetPanel) return

    this.panels.forEach(panel => {
      const selected = panel === targetPanel
      panel.classList.toggle('selected', selected)
      panel.setAttribute('aria-hidden', selected ? 'false' : 'true')
      panel.hidden = !selected
    })

    const selectedItem = targetPanel.querySelector('.wd-vis-filter-search-list__item[data-controller-id].selected')
    if (selectedItem) {
      this.moveSliderToItem(selectedItem)
      this.showItemContent(selectedItem)
      return
    }

    window.requestAnimationFrame(() => {
      targetPanel.querySelector('splide-slider')?.slider?.refresh()
    })
  }

  selectItem(item) {
    const scope = item.closest('.wd-vis-filter-search-list__panel') || item.closest('.wd-vis-filter-search-list')
    if (!scope) return

    scope.querySelectorAll('.wd-vis-filter-search-list__item[data-controller-id]').forEach(card => {
      const selected = card === item
      card.classList.toggle('selected', selected)
      card.setAttribute('aria-pressed', selected ? 'true' : 'false')
    })

    this.showItemContent(item)
  }

  showItemContent(item) {
    const targetId = item.dataset.controllerId
    if (!targetId || !this.contentHost) return

    this.pendingContentId = targetId
    const cached = this.contentCache.get(targetId)
    if (cached) {
      this.setActiveContent(cached)
      if (cached.querySelector('splide-slider')) {
        this.refreshPurchaseCardsSlider(cached)
        return
      }

      this.appendSliderFromTemplate(targetId, cached)
      return
    }

    if (this.mountQueued) return
    this.mountQueued = true
    visFiltersYieldToMain().then(() => {
      this.mountQueued = false
      if (!this.pendingContentId || !this.isConnected) return
      this.mountItemContent(this.pendingContentId)
    })
  }

  setActiveContent(target) {
    this.contentHost.querySelectorAll('.wd-vis-filter-search-list__item-content').forEach(content => {
      const selected = content === target
      content.classList.toggle('selected', selected)
      content.hidden = !selected
      content.setAttribute('aria-hidden', selected ? 'false' : 'true')
    })
    this.activeContentId = target.id
  }

  mountItemContent(targetId) {
    const cached = this.contentCache.get(targetId)
    if (cached) {
      this.setActiveContent(cached)
      if (cached.querySelector('splide-slider')) {
        this.refreshPurchaseCardsSlider(cached)
        return
      }

      this.appendSliderFromTemplate(targetId, cached)
      return
    }

    const template = this.getItemContentTemplate(targetId)
    const source = template?.content.querySelector('.wd-vis-filter-search-list__item-content')
    if (!source) return

    const introduce = source.querySelector('.wd-vis-filter-search-list__item-content-introduce')
    const shell = source.cloneNode(false)
    if (introduce) shell.appendChild(introduce.cloneNode(true))

    if (this.pendingContentId !== targetId) return
    this.contentCache.set(targetId, shell)
    this.contentHost.append(shell)
    this.setActiveContent(shell)
    this.appendSliderFromTemplate(targetId, shell)
  }

  appendSliderFromTemplate(targetId, live) {
    const slider = this.getItemContentTemplate(targetId)?.content.querySelector('splide-slider')
    if (!slider) return

    visFiltersYieldToMain().then(() => {
      if (this.pendingContentId !== targetId || !this.isConnected) return
      if (live.querySelector('splide-slider')) {
        this.refreshPurchaseCardsSlider(live)
        return
      }

      live.appendChild(slider.cloneNode(true))
      this.refreshPurchaseCardsSlider(live)
    })
  }

  refreshPurchaseCardsSlider(content) {
    const sliderElement = content?.querySelector('.wd-vis-filter-search-purchase-cards__splide')
    if (!sliderElement) return

    const contentId = content.id
    const refresh = slider => {
      if (this.activeContentId !== contentId) return
      slider?.refresh()
    }

    if (sliderElement.slider) {
      window.requestAnimationFrame(() => refresh(sliderElement.slider))
      return
    }

    sliderElement.addEventListener('splide:loaded', event => refresh(event.detail?.slider), { once: true })
  }

  moveSliderToItem(item) {
    const sliderElement = item.closest('.wd-vis-filter-search-list__splide')
    if (!sliderElement) return

    const slides = [...sliderElement.querySelectorAll('.wd-vis-filter-search-list__item[data-controller-id]')]
    const targetIndex = slides.indexOf(item)
    if (targetIndex < 0) return

    const move = slider => {
      if (!slider) return
      slider.refresh()
      slider.go(targetIndex)
    }

    if (sliderElement.slider) {
      window.requestAnimationFrame(() => move(sliderElement.slider))
      return
    }

    sliderElement.addEventListener('splide:loaded', event => move(event.detail?.slider), { once: true })
  }
}

if (!customElements.get('vis-active-filters-search')) {
  customElements.define('vis-active-filters-search', VisActiveFiltersSearch)
}

class VisActiveFiltersSearchAllModal extends HTMLElement {
  connectedCallback() {
    this.listenerAbort?.abort()
    this.listenerAbort = new AbortController()
    const { signal } = this.listenerAbort

    this.modal = this.closest('modal-container')
    this.scrollTabs = this.querySelector('scroll-tabs')

    this.addEventListener('click', this.onClick, { signal })
    this.scrollTabs?.addEventListener('scroll-tabs:select', this.onScrollTabsSelect, { signal })
    this.modal?.addEventListener('modal:show', this.onModalShow, { signal })
    this.modal?.addEventListener('modal:opened', this.onModalOpened, { signal })
    this.modal?.addEventListener('modal:hide', this.onModalHide, { signal })
  }

  disconnectedCallback() {
    this.unlockPageScroll()
    this.listenerAbort?.abort()
  }

  get controller() {
    return document.getElementById(this.dataset.controllerId)
  }

  get tabs() {
    return [...this.querySelectorAll('[data-all-systems-tab]')]
  }

  get panels() {
    return [...this.querySelectorAll('[data-all-systems-panel]')]
  }

  onModalShow = event => {
    this.lockPageScroll()
    const tabId = this.getOpenerTabId(event)
    const selected = this.getSelectedModel(tabId)
    const nextTabId = selected.tabId || tabId
    const tab = this.tabs.find(item => item.dataset.allSystemsTab === nextTabId) || this.tabs[0]
    if (!tab) return
    this.selectTab(tab)
    this.syncSelectedItem(tab.dataset.allSystemsTab, selected)
  }

  onModalOpened = () => {
    const tab = this.tabs.find(item => item.classList.contains('selected'))
    tab?.focus()
    this.scrollTabs?.scrollTabToStart?.(tab)
    this.scrollSelectedItemIntoView()
  }

  onModalHide = () => {
    this.unlockPageScroll()
    this.modal.openedBy?.querySelector('button')?.focus()
  }

  lockPageScroll() {
    if (this.pageScrollLocked) return
    this.pageScrollLocked = true
    this.savedScrollY = window.scrollY || document.documentElement.scrollTop || 0
    document.body.style.top = `-${this.savedScrollY}px`
    document.documentElement.classList.add('wd-vis-filter-search-modal-open')
    this.scrollLockAbort = new AbortController()
    const { signal } = this.scrollLockAbort
    document.addEventListener('touchstart', this.onLockTouchStart, { passive: true, signal })
    document.addEventListener('touchmove', this.onLockTouchMove, { passive: false, signal })
    document.addEventListener('wheel', this.onLockWheel, { passive: false, signal })
  }

  unlockPageScroll() {
    if (!this.pageScrollLocked) return
    this.pageScrollLocked = false
    this.scrollLockAbort?.abort()
    document.documentElement.classList.remove('wd-vis-filter-search-modal-open')
    document.body.style.top = ''
    window.scrollTo(0, this.savedScrollY || 0)
  }

  onLockTouchStart = event => {
    const touch = event.targetTouches[0]
    this.touchStartX = touch?.clientX ?? 0
    this.touchStartY = touch?.clientY ?? 0
  }

  onLockTouchMove = event => {
    if (!this.modal?.hasAttribute('open') || event.touches.length > 1) return
    const scrollable = this.getScrollableAncestor(event.target)
    if (!scrollable) {
      event.preventDefault()
      return
    }

    const touch = event.targetTouches[0]
    const deltaX = (touch?.clientX ?? 0) - this.touchStartX
    const deltaY = (touch?.clientY ?? 0) - this.touchStartY
    if (this.shouldBlockScroll(scrollable, deltaX, deltaY)) event.preventDefault()
  }

  onLockWheel = event => {
    if (!this.modal?.hasAttribute('open')) return
    const scrollable = this.getScrollableAncestor(event.target)
    if (!scrollable) {
      event.preventDefault()
      return
    }
    if (this.shouldBlockScroll(scrollable, -event.deltaX, -event.deltaY)) event.preventDefault()
  }

  getScrollableAncestor(node) {
    let current = node instanceof Element ? node : node?.parentElement
    while (current && this.contains(current)) {
      const style = window.getComputedStyle(current)
      const canScrollY =
        (style.overflowY === 'auto' || style.overflowY === 'scroll') && current.scrollHeight > current.clientHeight + 1
      const canScrollX =
        (style.overflowX === 'auto' || style.overflowX === 'scroll') && current.scrollWidth > current.clientWidth + 1
      if (canScrollY || canScrollX) return current
      current = current.parentElement
    }
    return null
  }

  shouldBlockScroll(scrollable, deltaX, deltaY) {
    const horizontal = Math.abs(deltaX) > Math.abs(deltaY)
    if (horizontal) {
      const canScrollX = scrollable.scrollWidth > scrollable.clientWidth + 1
      if (!canScrollX) return true
      const atStart = scrollable.scrollLeft <= 0 && deltaX > 0
      const atEnd = scrollable.scrollLeft + scrollable.clientWidth >= scrollable.scrollWidth - 1 && deltaX < 0
      return atStart || atEnd
    }

    const canScrollY = scrollable.scrollHeight > scrollable.clientHeight + 1
    if (!canScrollY) return true
    const atTop = scrollable.scrollTop <= 0 && deltaY > 0
    const atBottom = scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - 1 && deltaY < 0
    return atTop || atBottom
  }

  onClick = event => {
    const item = event.target.closest('[data-all-systems-item]')
    if (!item || !this.contains(item)) return

    const selected = this.controller?.selectModel(item.dataset.searchGroup, item.dataset.searchShortName, item)
    if (selected) this.modal?.hide()
  }

  onScrollTabsSelect = event => {
    const { tab } = event.detail
    this.showTabPanel(tab)
    this.syncSelectedItem(tab.dataset.allSystemsTab)
  }

  selectTab(tab) {
    if (!tab) return
    this.scrollTabs?.selectTab?.(tab, { silent: true })
    this.showTabPanel(tab)
  }

  showTabPanel(tab) {
    const tabId = tab.dataset.allSystemsTab

    this.panels.forEach(panel => {
      const selected = panel.dataset.allSystemsPanel === tabId
      panel.classList.toggle('selected', selected)
      panel.hidden = !selected
    })
  }

  getOpenerTabId(event) {
    const opener = event?.detail?.opener || this.modal?.openedBy
    if (!(opener instanceof HTMLElement)) return ''
    return opener.dataset.modalTab || opener.closest('[data-modal-tab]')?.dataset.modalTab || ''
  }

  getSelectedModel(tabId) {
    const { controller } = this
    if (!controller) return { tabId, shortName: '', groupName: '' }

    const sliderSelector =
      '.page-filters-search-list__item[data-controller-id].selected, .wd-vis-filter-search-list__item[data-controller-id].selected'
    const tabs = controller.tabs || []
    let sliderItem = null

    if (tabs.length) {
      const outerTab =
        tabs.find(tab => tab.dataset.tabId === tabId) || tabs.find(tab => tab.classList.contains('selected'))
      const outerPanel = outerTab && (controller.panels || []).find(panel => panel.id === outerTab.dataset.tabId)
      sliderItem = outerPanel?.querySelector(sliderSelector)
    } else {
      sliderItem = controller.querySelector(sliderSelector)
    }

    let shortName = sliderItem?.dataset.searchShortName || ''
    let groupName = sliderItem?.dataset.searchGroup || ''

    const content = controller.contentHost?.querySelector('.wd-vis-filter-search-list__item-content.selected')
    if (content?.dataset.searchShortName) {
      shortName = content.dataset.searchShortName
      groupName = content.dataset.searchGroup || groupName
    }

    const matched = [...this.querySelectorAll('[data-all-systems-item]')].find(item => {
      if (shortName && item.dataset.searchShortName !== shortName) return false
      if (groupName && item.dataset.searchGroup !== groupName) return false
      return Boolean(shortName)
    })

    return {
      tabId: matched?.closest('[data-all-systems-panel]')?.dataset.allSystemsPanel || tabId,
      shortName,
      groupName
    }
  }

  syncSelectedItem(tabId, selectedModel = this.getSelectedModel(tabId)) {
    const selectedName = selectedModel?.shortName
    const selectedGroup = selectedModel?.groupName

    this.querySelectorAll('[data-all-systems-item]').forEach(item => {
      const selected =
        item.closest('[data-all-systems-panel]')?.dataset.allSystemsPanel === tabId &&
        Boolean(selectedName) &&
        item.dataset.searchShortName === selectedName &&
        (!selectedGroup || item.dataset.searchGroup === selectedGroup)
      item.classList.toggle('selected', selected)
      item.setAttribute('aria-pressed', selected ? 'true' : 'false')
    })
  }

  scrollSelectedItemIntoView() {
    const item = this.querySelector('[data-all-systems-panel]:not([hidden]) [data-all-systems-item].selected')
    const panel = item?.closest('.wd-vis-filter-search-all-modal__panel')
    if (!item || !panel) return

    const panelRect = panel.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()
    if (itemRect.top >= panelRect.top && itemRect.bottom <= panelRect.bottom) return
    panel.scrollTop += itemRect.top - panelRect.top - (panelRect.height - itemRect.height) / 2
  }
}

if (!customElements.get('vis-active-filters-search-all-modal')) {
  customElements.define('vis-active-filters-search-all-modal', VisActiveFiltersSearchAllModal)
}
